import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { CalendarDays, ChevronRight, ChevronLeft } from "lucide-react";

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const AR_DOW = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
const z = (n) => String(n).padStart(2, "0");
const toStr = (d) => `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
const parse = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y) return null;
  return new Date(y, (m || 1) - 1, d || 1);
};

export default function DateField({ value, onChange, testId, placeholder = "اختر التاريخ" }) {
  const [open, setOpen] = useState(false);
  const sel = parse(value);
  const [view, setView] = useState(sel || new Date());
  const y = view.getFullYear();
  const m = view.getMonth();
  const startDow = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const today = new Date();
  const isSel = (d) => sel && sel.getFullYear() === y && sel.getMonth() === m && sel.getDate() === d;
  const isToday = (d) => today.getFullYear() === y && today.getMonth() === m && today.getDate() === d;
  const nav = (delta) => setView(new Date(y, m + delta, 1));
  const navY = (delta) => setView(new Date(y + delta, m, 1));

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setView(sel || new Date()); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm text-right flex items-center justify-between gap-2 focus:outline-none focus:border-amber-400"
        >
          <span className={value ? "" : "text-stone-400"}>{value || placeholder}</span>
          <CalendarDays size={16} className="text-amber-600 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[288px] p-3 glass border-amber-400/30" align="start" dir="rtl">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={() => navY(-1)} title="السنة السابقة" className="px-2 py-1 rounded-lg hover:bg-amber-500/15 text-stone-500 text-xs">«</button>
          <button type="button" onClick={() => nav(-1)} title="الشهر السابق" className="p-1 rounded-lg hover:bg-amber-500/15 text-amber-600"><ChevronRight size={18} /></button>
          <span className="font-head font-bold text-stone-800 text-sm">{AR_MONTHS[m]} {y}</span>
          <button type="button" onClick={() => nav(1)} title="الشهر التالي" className="p-1 rounded-lg hover:bg-amber-500/15 text-amber-600"><ChevronLeft size={18} /></button>
          <button type="button" onClick={() => navY(1)} title="السنة التالية" className="px-2 py-1 rounded-lg hover:bg-amber-500/15 text-stone-500 text-xs">»</button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {AR_DOW.map((w) => <div key={w} className="text-[10px] text-stone-400 text-center">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => d === null ? <div key={`e${i}`} /> : (
            <button
              key={d}
              type="button"
              data-testid={testId ? `${testId}-day-${d}` : undefined}
              onClick={() => { onChange(toStr(new Date(y, m, d))); setOpen(false); }}
              className={`h-8 rounded-lg text-xs transition ${isSel(d) ? "bg-amber-500 text-white font-bold" : isToday(d) ? "bg-amber-500/20 text-amber-700" : "hover:bg-amber-500/15 text-stone-700"}`}
            >
              {d}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
